import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar } from '@/components/Snackbar';
import { mailPathFor } from '@/features/email-task/OpenEmailButton';
import { SendCountdown } from './SendCountdown';
import {
  clearNotice,
  commitPendingSend,
  getPendingSend,
  holdPendingSend,
  registerSendUndoHost,
  subscribePendingSend,
  undoPendingSend,
} from './pendingSend';

/**
 * "Sending… 12 s · Undo", for as long as a send is held (issue #182) — the
 * window chosen in Settings → General → Undo send, counted down beside the
 * sentence.
 *
 * Mounted on the **shell**, not on the mail view, which is the whole point:
 * the mail undo's snackbar goes away when you leave Mail and its pending
 * action is committed with it, and a send must not be decided by the user
 * wandering off to the calendar. Mounting it is also what makes the window
 * exist at all — with no host registered a send goes out at once, which is how
 * the popped-out compose window behaves.
 *
 * Undo gives the composer back. The mail view is what reopens it, so when the
 * user is somewhere else entirely this takes them back to Mail first; if they
 * are already there, the view has taken the draft before this line runs and
 * the navigation is skipped, so an open message stays open.
 *
 * The same strip doubles as the place a finished send says what happened —
 * "some recipients were refused" — because the composer that used to say it
 * has been closed for the whole window by then. That sentence has no Undo.
 * When the server said where the copy in Sent is, it offers "View message"
 * instead, which opens that copy in its own mailbox (#84).
 */
export function SendUndoSnackbar() {
  const pending = useSyncExternalStore(subscribePendingSend, getPendingSend, getPendingSend);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => registerSendUndoHost(), []);

  const undoable = pending?.undoable === true;
  const view = undoable ? undefined : pending?.view;

  return (
    <Snackbar
      className="snackbar--send"
      message={pending?.label}
      {...(pending ? { messageKey: pending.id } : {})}
      detail={<SendCountdown pending={pending} />}
      {...(undoable
        ? {
            actionLabel: 'Undo',
            onAction: () => {
              if (!undoPendingSend()) return;
              if (!location.pathname.startsWith('/mail')) navigate('/mail');
            },
            onHoldChange: holdPendingSend,
          }
        : view !== undefined
          ? {
              actionLabel: 'View message',
              onAction: () => {
                clearNotice();
                navigate(mailPathFor(view.accountId, view.folderId, view.messageId));
              },
            }
          : {})}
      // Escape on a pending send means "stop waiting", not "cancel": only Undo
      // cancels. On the notice it is an ordinary dismiss.
      onDismiss={undoable ? commitPendingSend : clearNotice}
    />
  );
}
